import { defineStore } from 'pinia'
import { ref } from 'vue'
import { deepMerge } from 'ts-deep-utils'
import type { components } from '../types/_api'

type CreditorSettingsData = components['schemas']['Creditor-Settings']['settings']

export const useCreditorSettingsStore = defineStore('creditorSettings', () => {
  const uncommittedSettings = ref(new Map<string, Partial<CreditorSettingsData>>())
  const pendingSettings = ref(new Map<string, Partial<CreditorSettingsData>>())

  function getCreditorUncommittedSettings(creditorId: string): Partial<CreditorSettingsData> | undefined {
    const stored = uncommittedSettings.value.get(creditorId)
    if (Object.keys(stored ?? {}).length === 0) return
    return stored
  }

  function hasCreditorUncommittedSettings(creditorId: string): boolean {
    return (
      uncommittedSettings.value.has(creditorId) &&
      Object.keys(uncommittedSettings.value.get(creditorId) ?? {}).length > 0
    )
  }

  function mergeCreditorUncommittedSettings(creditorId: string, patch: Partial<CreditorSettingsData>) {
    const existing = uncommittedSettings.value.get(creditorId) ?? {}
    uncommittedSettings.value.set(creditorId, deepMerge(existing, patch))
  }

  function clearCreditorUncommittedSettings(creditorId: string) {
    uncommittedSettings.value.delete(creditorId)
  }

  function hasAnyUncommittedSettings(): boolean {
    for (const [, value] of uncommittedSettings.value) {
      if (Object.keys(value).length > 0) return true
    }
    return false
  }

  function getAllCreditorIdsWithUncommittedSettings(): string[] {
    const ids: string[] = []
    for (const [creditorId, value] of uncommittedSettings.value) {
      if (Object.keys(value).length > 0) {
        ids.push(creditorId)
      }
    }
    return ids
  }

  return {
    uncommittedSettings,
    pendingSettings,

    getCreditorUncommittedSettings,
    hasCreditorUncommittedSettings,
    mergeCreditorUncommittedSettings,
    clearCreditorUncommittedSettings,
    hasAnyUncommittedSettings,
    getAllCreditorIdsWithUncommittedSettings,
  }
})
